import { Box, Container, Stack, Typography, Chip, Grid } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import InsightsIcon from "@mui/icons-material/Insights";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChatMock from "../components/ChatMock";
import AuthPanel from "../components/AuthPanel";

/*
 * The page a plant manager lands on before they have an account.
 *
 * Dark, because it sits beside a control room screen and a white page at 2am is a torch
 * in the face; the brand green stays as the accent so the console, the portal and the
 * SCADA still read as one product rather than three.
 *
 * The hero shows the thing itself - a question and the answer it produces - instead of a
 * stock robot. The people buying this have seen enough robot pictures; what they have not
 * seen is their own plant answering a question in Hinglish.
 */

/*
 * Real questions, in the words a plant uses, rather than four cards saying "analytics".
 *
 * A feature list asks the reader to imagine what they would type; a list of questions
 * hands them the sentence. Every one of these is something the agent already answers, so
 * nothing here is a promise the product cannot keep.
 */
const QUESTIONS = [
    { icon: <InsightsIcon />, group: "Energy", q: "Which machine used the most power in yesterday’s Shift A?" },
    { icon: <InsightsIcon />, group: "Energy", q: "When did this month’s peak demand happen?" },
    { icon: <BoltIcon />, group: "Machine", q: "How many hours did Furnace 2 run last week?" },
    { icon: <BoltIcon />, group: "Machine", q: "Why has DB-5 reported no data since yesterday?" },
    { icon: <NotificationsActiveIcon />, group: "Alarm", q: "Which alarms came up most often today?" },
    { icon: <NotificationsActiveIcon />, group: "Alarm", q: "Which alarms are open right now?" },
    { icon: <DescriptionOutlinedIcon />, group: "Report", q: "What was total consumption in yesterday’s daily report?" },
    { icon: <DescriptionOutlinedIcon />, group: "Document", q: "What does the SOP say when a compressor trips?" },
];

export default function Landing() {

    return (
        <Box sx={{ bgcolor: "#07120B", color: "#fff", minHeight: "100vh" }}>
            {/* ---------- nav ---------- */}
            <Box component="nav" sx={{
                position: "sticky", top: 0, zIndex: 10,
                backdropFilter: "blur(10px)", bgcolor: "rgba(7,18,11,.78)",
                borderBottom: "1px solid rgba(255,255,255,.07)",
            }}>
                <Box sx={{ px: { xs: 2, md: 4, xl: 6 } }}>
                    {/*
                      * Just the logo.
                      *
                      * There was a "Log in" button here, from when signing in was its own
                      * page. The form now sits in the hero, half a screen below - a button
                      * that scrolls to something already in view is one the eye has to
                      * read and then ignore.
                      */}
                    <Stack direction="row"
                        sx={{ alignItems: "center", py: 1, minHeight: 58 }}>
                        <Box component="img" src="/assets/iotmatrix-white.png" alt="iotmatrix"
                            sx={{ height: { xs: 26, sm: 32 } }} />
                    </Stack>
                </Box>
            </Box>

            {/* ---------- hero ---------- */}
            <Box sx={{
                position: "relative", overflow: "hidden",
                /*
                 * All green, and moving.
                 *
                 * The blue light on the right was a second brand colour on the first screen
                 * the customer ever sees. Three greens at different depths do the same job -
                 * the page still has a centre of gravity behind the headline - and the slow
                 * drift keeps the dark panel from reading as a flat block.
                 */
                background:
                    "radial-gradient(1000px 560px at 10% 16%, rgba(46,125,32,.42), transparent 62%),"
                    + "radial-gradient(760px 520px at 86% 6%, rgba(123,217,95,.20), transparent 64%),"
                    + "radial-gradient(900px 600px at 70% 95%, rgba(20,90,60,.34), transparent 66%)",
                backgroundSize: "140% 140%",
                animation: "heroDrift 24s ease-in-out infinite alternate",
                "@keyframes heroDrift": {
                    from: { backgroundPosition: "0% 0%, 100% 0%, 60% 100%" },
                    to: { backgroundPosition: "12% 10%, 86% 12%, 48% 88%" },
                },
                // Somebody who asked their machine to stop animating gets a still page.
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}>
                <Container maxWidth="xl" sx={{ py: { xs: 7, md: 12 }, px: { xs: 2, md: 4, xl: 6 } }}>
                    <Grid container spacing={6} sx={{ alignItems: "center" }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Chip label="Ask AI for your plant" size="small" sx={{
                                bgcolor: "rgba(46,125,32,.22)", color: "#9FE08C",
                                border: "1px solid rgba(46,125,32,.5)", mb: 2.5, fontWeight: 600,
                            }} />
                            <Typography sx={{
                                fontWeight: 800, lineHeight: 1.05, letterSpacing: -1.5,
                                fontSize: { xs: 40, sm: 54, md: 62 },
                            }}>
                                Ask your plant<br />
                                <Box component="span" sx={{ color: "#7BD95F" }}>anything</Box>
                            </Typography>
                            <Typography sx={{ mt: 2.5, fontSize: 17, color: "rgba(255,255,255,.72)", maxWidth: 520 }}>
                                MatrixIQ reads your SCADA data, alarms, reports and documents, and answers in plain
                                language. No queries to write, no reports to hunt through.
                            </Typography>

                            {/*
                              * One per line.
                              *
                              * As a wrapping row they broke two-and-one against the narrower
                              * text column, and the ticks no longer lined up under each other.
                              * A list reads the same at every width and the three ticks stay
                              * on one vertical edge.
                              */}
                            <Stack spacing={1.2} sx={{ mt: 4 }}>
                                {["Your own tags and equipment", "English, Hindi or Hinglish", "Every answer cites its source"].map((t) => (
                                    <Stack key={t} direction="row" spacing={1.2} sx={{ alignItems: "center" }}>
                                        <CheckCircleIcon sx={{ fontSize: 18, color: "#7BD95F", flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: 15, color: "rgba(255,255,255,.75)" }}>{t}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Grid>

                        {/*
                          * The form, where the product demo used to be.
                          *
                          * Somebody who has read the headline can start here without a second
                          * page in between - and the three ticks on the left are what they
                          * read while filling it in. The demo moved below, where it answers
                          * "what does it actually look like" rather than competing with the
                          * one thing this screen is for.
                          */}
                        <Grid size={{ xs: 12, md: 6 }} id="signin">
                            <AuthPanel />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ---------- features ---------- */}
            <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 }, px: { xs: 2, md: 4, xl: 6 } }} id="features">
                {/* The product in use, first. The list below reads as "and all of these
                    too" once somebody has seen one answer; the other way round it is a list
                    of claims with the proof underneath. */}
                <Box sx={{ mb: { xs: 7, md: 10 } }}>
                    <ChatMock />
                </Box>

                <Typography sx={{ fontWeight: 800, fontSize: { xs: 28, md: 36 }, letterSpacing: -0.5 }}>
                    Questions asked every day
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,.65)", mt: 1, mb: 5, maxWidth: 620 }}>
                    The AI knows your tags, your equipment and your shifts - so the answer is about your
                    plant, not a generic one.
                </Typography>

                {/* Each one reads as something typed into the box, not as a feature name. */}
                <Grid container spacing={2}>
                    {QUESTIONS.map((item) => (
                        <Grid size={{ xs: 12, md: 6, xl: 4 }} key={item.q}>
                            <Stack direction="row" spacing={2} sx={{
                                alignItems: "center", height: "100%", p: 2.2, borderRadius: 3,
                                bgcolor: "rgba(255,255,255,.04)",
                                border: "1px solid rgba(255,255,255,.08)",
                                transition: "border-color .2s, transform .2s, background-color .2s",
                                "&:hover": {
                                    borderColor: "rgba(123,217,95,.45)",
                                    bgcolor: "rgba(46,125,32,.10)",
                                    transform: "translateY(-2px)",
                                },
                            }}>
                                <Box sx={{
                                    minWidth: 38, height: 38, borderRadius: 2,
                                    display: "grid", placeItems: "center",
                                    bgcolor: "rgba(46,125,32,.2)", color: "#7BD95F",
                                }}>{item.icon}</Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "#7BD95F", fontWeight: 700, letterSpacing: .4 }}>
                                        {item.group.toUpperCase()}
                                    </Typography>
                                    <Typography sx={{ fontSize: 15.5, color: "rgba(255,255,255,.92)" }}>
                                        “{item.q}”
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>

                <Typography sx={{ color: "rgba(255,255,255,.5)", mt: 3, fontSize: 14 }}>
                    And plenty more - ask it the way you would ask an operator.
                </Typography>

            </Container>

            {/* ---------- footer ---------- */}
            <Box sx={{ borderTop: "1px solid rgba(255,255,255,.07)" }}>
                <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4, xl: 6 } }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
                        sx={{ justifyContent: "space-between", alignItems: { sm: "center" } }}>
                        <Box component="img" src="/assets/iotmatrix-white.png" alt="iotmatrix" sx={{ height: 26, opacity: .85 }} />
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,.5)" }}>
                            © {new Date().getFullYear()} iotmatrix · support@myiotplatform.in
                        </Typography>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
}


